import type { Asset } from '../store/seedanceTypes';
import {
  SEEDANCE_STORAGE_KEYS,
  seedanceWorkspaceDb,
} from './seedanceStorage';

export type AssetPreviewSource = Pick<Asset, 'id' | 'sourceAssetId' | 'type' | 'url' | 'thumb'> &
  Partial<Pick<Asset, 'name'>>;

interface LibraryAssetPreviewCache {
  version: 1;
  assets: Asset[];
}

const LIBRARY_ASSET_PREVIEW_CACHE_MAX = 500;
let previewCacheWriteQueue: Promise<void> = Promise.resolve();

/** 与工作台快照一致：仅存当前选中素材组的列表外壳，用于离线先渲染 */
export interface LibraryGroupAssetsSnapshot {
  groupId: string;
  assets: Asset[];
}

/** 预览缓存、列表填充和工作台回填共用的稳定身份键。 */
export function getLibraryAssetPreviewKey(type: Asset['type'], sourceAssetId: string): string {
  return `${type}:${sourceAssetId.trim()}`;
}

function buildPreviewLookup(cachedAssets: AssetPreviewSource[]): Map<string, AssetPreviewSource> {
  const cachedBySourceId = new Map<string, AssetPreviewSource>();
  cachedAssets.forEach((asset) => {
    const sourceId = (asset.sourceAssetId || asset.id || '').trim();
    if (sourceId) cachedBySourceId.set(getLibraryAssetPreviewKey(asset.type, sourceId), asset);
  });
  return cachedBySourceId;
}

/** 远端列表已有 url/thumb 时保留，仅对缺失项用缓存补齐。 */
export function fillMissingPreviews(assets: Asset[], cachedAssets: AssetPreviewSource[]): Asset[] {
  const cachedBySourceId = buildPreviewLookup(cachedAssets);
  return assets.map((asset) => {
    const sourceId = (asset.sourceAssetId || asset.id || '').trim();
    const cached = cachedBySourceId.get(getLibraryAssetPreviewKey(asset.type, sourceId));
    if (!cached) return asset;
    return {
      ...asset,
      url: asset.url || cached.url || null,
      thumb: asset.thumb || cached.thumb || null,
    };
  });
}

/** 从 snapshot 首屏恢复时，预览字典覆盖 snapshot 中的旧 url/thumb。 */
export function overlayPreviews(assets: Asset[], cachedAssets: AssetPreviewSource[]): Asset[] {
  const cachedBySourceId = buildPreviewLookup(cachedAssets);
  return assets.map((asset) => {
    const sourceId = (asset.sourceAssetId || asset.id || '').trim();
    const cached = cachedBySourceId.get(getLibraryAssetPreviewKey(asset.type, sourceId));
    if (!cached) return asset;
    return {
      ...asset,
      url: cached.url || asset.url || null,
      thumb: cached.thumb || asset.thumb || null,
    };
  });
}

/** 首屏缓存恢复：snapshot 只决定目录外壳，预览字典始终保留给后续列表填充。 */
export function prepareLibraryInitialPreviewState(
  groupId: string,
  snapshot: LibraryGroupAssetsSnapshot | null,
  previewCache: Asset[],
): { snapshotAssets: Asset[]; previewAssets: Asset[] } {
  const snapshotAssets =
    snapshot?.groupId === groupId.trim() && snapshot.assets.length
      ? overlayPreviews(snapshot.assets, previewCache)
      : [];
  return { snapshotAssets, previewAssets: previewCache };
}

function normalizePreviewSource(source: AssetPreviewSource): Asset | null {
  const sourceAssetId = (source.sourceAssetId || source.id || '').trim();
  if (!sourceAssetId) return null;
  if (source.type !== 'image' && source.type !== 'video' && source.type !== 'audio') return null;
  const url = typeof source.url === 'string' ? source.url.trim() : '';
  const thumb = typeof source.thumb === 'string' ? source.thumb.trim() : '';
  if (!url && !thumb) return null;
  return {
    id: sourceAssetId,
    sourceAssetId,
    type: source.type,
    name: source.name || sourceAssetId,
    url: url || null,
    thumb: thumb || null,
  };
}

function parsePreviewCache(raw: unknown): Asset[] {
  if (!raw || typeof raw !== 'object') return [];
  const value = raw as Partial<LibraryAssetPreviewCache>;
  if (value.version !== 1 || !Array.isArray(value.assets)) return [];
  return value.assets
    .map((asset) => normalizePreviewSource(asset))
    .filter((asset): asset is Asset => Boolean(asset));
}

async function readLibraryAssetPreviewCache(): Promise<Asset[]> {
  await seedanceWorkspaceDb.init();
  const raw = await seedanceWorkspaceDb.get<unknown>(
    SEEDANCE_STORAGE_KEYS.libraryAssetPreviewCache,
  );
  return parsePreviewCache(raw);
}

function mergePreviewEntries(existing: AssetPreviewSource[], incoming: AssetPreviewSource[]): Asset[] {
  const byIdentity = new Map<string, Asset>();
  [...existing, ...incoming].forEach((source) => {
    const asset = normalizePreviewSource(source);
    if (!asset) return;
    const key = getLibraryAssetPreviewKey(asset.type, asset.sourceAssetId || asset.id);
    const previous = byIdentity.get(key);
    byIdentity.delete(key);
    byIdentity.set(key, {
      ...previous,
      ...asset,
      url: asset.url || previous?.url || null,
      thumb: asset.thumb || previous?.thumb || null,
    });
  });
  return Array.from(byIdentity.values());
}

/** 工作台与素材库共用的预览 URL 索引。 */
export async function loadLibraryAssetPreviewCache(): Promise<Asset[]> {
  await previewCacheWriteQueue;
  return readLibraryAssetPreviewCache();
}

/** 合并写入预览 URL；只读写预览字典，不合并 snapshot。 */
export function persistLibraryAssetPreviewCache(
  sources: AssetPreviewSource[],
): Promise<void> {
  const incoming = sources
    .map(normalizePreviewSource)
    .filter((asset): asset is Asset => Boolean(asset));
  if (!incoming.length) return Promise.resolve();

  const write = async () => {
    try {
      const existing = await readLibraryAssetPreviewCache();
      const assets = mergePreviewEntries(existing, incoming).slice(
        -LIBRARY_ASSET_PREVIEW_CACHE_MAX,
      );
      await seedanceWorkspaceDb.setAsync(SEEDANCE_STORAGE_KEYS.libraryAssetPreviewCache, {
        version: 1,
        assets,
      } satisfies LibraryAssetPreviewCache);
    } catch {
      /* 预览缓存失败不影响页面主流程 */
    }
  };

  const next = previewCacheWriteQueue.then(write, write);
  previewCacheWriteQueue = next.catch(() => undefined);
  return next;
}

export async function loadLibraryGroupAssetsSnapshot(): Promise<LibraryGroupAssetsSnapshot | null> {
  await seedanceWorkspaceDb.init();
  const raw = await seedanceWorkspaceDb.get<unknown>(
    SEEDANCE_STORAGE_KEYS.libraryGroupAssetsSnapshot,
  );
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const groupId =
    typeof o.groupId === 'string' ? o.groupId.trim() : String(o.groupId ?? '').trim();
  if (!groupId || !Array.isArray(o.assets)) return null;
  return { groupId, assets: o.assets as Asset[] };
}

export async function persistLibraryGroupAssetsSnapshot(
  groupId: string,
  assets: Asset[],
): Promise<void> {
  const gid = (groupId || '').trim();
  if (!gid) return;
  await persistLibraryAssetPreviewCache(assets);
  await seedanceWorkspaceDb.init();
  if (!seedanceWorkspaceDb.db) return;
  try {
    await seedanceWorkspaceDb.setAsync(SEEDANCE_STORAGE_KEYS.libraryGroupAssetsSnapshot, {
      groupId: gid,
      assets,
    });
  } catch {
    /* 缓存失败不影响主流程 */
  }
}
